/* eslint-disable @next/next/no-img-element */

import React, { useMemo } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import SEO from '../../components/SEO.jsx';
import CarCard from '../../components/CarCard';
import { getAllCars } from '../../lib/shopify.mjs';
import { isEvCar } from '../../lib/evFilter.js';
import { readCarStatuses } from '../../lib/carStatusStore.js';
import { computeSchemaAvailability } from '../../lib/carStatusUtils.js';
import { COMMON_OFFER_EXTENSIONS } from '../../config/business';
import { getPriceInfo } from '../../lib/carPrice';

const Breadcrumb = dynamic(() => import('../../components/Breadcrumb'), {
  loading: () => null,
});

const SITE = 'https://www.chiangmaiusedcar.com';

const BRAND_CONFIG = {
  toyota: { label: 'Toyota', tokens: ['toyota'] },
  honda: { label: 'Honda', tokens: ['honda'] },
  isuzu: { label: 'Isuzu', tokens: ['isuzu'] },
  nissan: { label: 'Nissan', tokens: ['nissan'] },
  mazda: { label: 'Mazda', tokens: ['mazda'] },
  mitsubishi: { label: 'Mitsubishi', tokens: ['mitsubishi'] },
  ford: { label: 'Ford', tokens: ['ford'] },
};

const BRAND_DESCRIPTIONS = {
  toyota:
    'Toyota (โตโยต้า) ครองใจคนไทยและชาวเชียงใหม่มาหลายทศวรรษ ด้วยชื่อเสียงด้านความทนทาน ประหยัดน้ำมัน และอะไหล่หาง่ายราคาไม่แพง รถมือสองรุ่นยอดนิยมได้แก่ Hilux Revo (ไฮลักซ์ รีโว่), Fortuner (ฟอร์จูนเนอร์), Vios (วีออส), Yaris (ยาริส) และ Corolla Altis (โคโรลล่า อัลติส) ตอบโจทย์ทุกไลฟ์สไตล์ตั้งแต่รถครอบครัวจนถึงรถใช้งานพาณิชย์',
  honda:
    'Honda (ฮอนด้า) โดดเด่นด้านเทคโนโลยีประหยัดน้ำมัน เครื่องยนต์ VTEC และดีไซน์สปอร์ตที่เป็นเอกลักษณ์ รถฮอนด้ามือสองรุ่นฮิตในตลาดได้แก่ City (ซิตี้), Jazz (แจ๊ซ), Civic (ซีวิค), HR-V และ CR-V ขับขี่สนุก ดูแลรักษาง่าย เหมาะสำหรับคนรุ่นใหม่และครอบครัวที่ต้องการรถประหยัดน้ำมันทรงสวย',
  isuzu:
    'Isuzu (อีซูซุ) ยอดกระบะเมืองไทยที่ครองใจคนทำธุรกิจอย่างยาวนาน มีชื่อเสียงเรื่องเครื่องยนต์ดีเซลที่ทรงพลังและทนทานสุดๆ รุ่นที่นิยมในตลาดรถมือสองคือ D-MAX (ดีแม็กซ์) ทั้งแบบตอนเดียวและ 4 ประตู (Cab4) เหมาะอย่างยิ่งสำหรับงานบรรทุกหนัก ทำการเกษตร หรือขับขึ้นดอยในสภาพถนนภาคเหนือ',
  nissan:
    'Nissan (นิสสัน) แบรนด์รถยนต์ที่ชูจุดเด่นเรื่องความกว้างขวาง นั่งสบาย และความคุ้มค่า รถนิสสันมือสองรุ่นยอดนิยม ได้แก่ Navara (นาวาร่า), Almera (อัลเมร่า), Kicks (คิกส์), Terra (เทอร์ร่า) และ Teana (เทียน่า) ช่วงล่างนุ่มนวล ขับขี่ปลอดภัยทั้งในเมืองเชียงใหม่และวิ่งเบิ่งทางไกลต่างจังหวัด',
  mazda:
    'Mazda (มาสด้า) ค่ายรถที่ใส่ใจงานดีไซน์หรูหรา ปราดเปรียวด้วยเทคโนโลยี SKYACTIV ที่แรงและประหยัดน้ำมัน รถมาสด้ามือสองรุ่นยอดนิยม ได้แก่ Mazda 2 (มาสด้า 2), Mazda 3 (มาสด้า 3), CX-3 และ CX-5 เหมาะสำหรับผู้ที่หลงใหลในความสปอร์ตและต้องการรถที่มีเอกลักษณ์ไม่ซ้ำใคร',
  mitsubishi:
    'Mitsubishi (มิตซูบิชิ) แบรนด์อึดถึกทนที่ได้รับความนิยมสูงในกลุ่มรถอเนกประสงค์ (SUV) และกระบะสายลุย รถมือสองยอดฮิต ได้แก่ Triton (ไทรทัน), Pajero Sport (ปาเจโร่ สปอร์ต), Xpander (เอ็กซ์แพนเดอร์) และ Mirage (มิราจ) ห้องโดยสารกว้าง แอร์เย็นฉ่ำ ออปชั่นจัดเต็ม คุ้มค่าตอบโจทย์ครอบครัวใหญ่',
  ford: 'Ford (ฟอร์ด) มีชื่อเสียงระดับโลกในฐานะกระบะและรถอเนกประสงค์ (PPV) สมรรถนะสูงสไตล์อเมริกันแกร่ง รถฟอร์ดมือสองรุ่นยอดนิยม ได้แก่ Ranger (เรนเจอร์) และ Everest (เอเวอเรสต์) เด่นเรื่องโครงสร้างตัวถังที่แข็งแรง ช่วงล่างหนึบเกาะถนนเยี่ยม ลุยเหนือล่องใต้ขึ้นเขาได้อย่างมั่นใจ',
};

function normalizeBrandSlug(input) {
  const raw = String(input || '')
    .trim()
    .toLowerCase();

  // allow only safe path chars
  const safe = raw.replace(/[^a-z0-9-]/g, '');
  return safe;
}

function getBrandInfo(brandSlug) {
  const key = normalizeBrandSlug(brandSlug);
  const config = BRAND_CONFIG[key];
  if (!config) return null;
  return { slug: key, ...config };
}

function buildBrandFaqEntries(brandLabel) {
  const safeLabel = String(brandLabel || '').trim() || 'รถมือสอง';
  return [
    {
      question: `รถมือสอง ${safeLabel} เชียงใหม่ มีรุ่นอะไรบ้าง?`,
      answer:
        'ดูรายการรถที่อยู่ในหน้านี้ได้เลย (อัปเดตตามสต็อกจริง) คลิกแต่ละคันเพื่อดูรูป/รายละเอียด/ราคา และสถานะรถ',
    },
    {
      question: `อยากนัดดูรถ ${safeLabel} ต้องทำยังไง?`,
      answer:
        'เลือกคันที่สนใจ แล้วกดเข้าไปดูรายละเอียด จากนั้นทัก LINE/โทร เพื่อนัดวันเวลาเข้ามาดูรถได้เลย',
    },
    {
      question: 'มีบริการจัดไฟแนนซ์หรือฟรีดาวน์ไหม?',
      answer:
        'มีทีมช่วยประสานไฟแนนซ์ และบางรุ่นมีโปรโมชันฟรีดาวน์ (ขึ้นอยู่กับเงื่อนไขและเครดิตลูกค้า) แนะนำทัก LINE เพื่อเช็คเงื่อนไขล่าสุด',
    },
  ];
}

function buildBrandStructuredDataJsonLd({ brandLabel, brandSlug, cars, page, perPage }) {
  const safeCars = Array.isArray(cars) ? cars : [];
  const safePage = Number.isFinite(page) && page > 0 ? page : 1;
  const safePerPage = Number.isFinite(perPage) && perPage > 0 ? perPage : safeCars.length || 24;

  const listItems = safeCars.map((car, index) => {
    const handle = car?.handle;
    const carUrl = handle ? `${SITE}/car/${handle}` : `${SITE}/all-cars`;

    const priceInfo = getPriceInfo(car?.price?.amount || 0, { allowZero: false });
    const rawImage = car?.images?.[0]?.url;
    const imageUrl = rawImage
      ? rawImage.startsWith('http')
        ? rawImage
        : `${SITE}${rawImage.startsWith('/') ? '' : '/'}${rawImage}`
      : `${SITE}/herobanner/cnxcar.webp`;

    const title = car?.title || `รถมือสอง ${brandLabel} เชียงใหม่`;
    const availabilityValue = computeSchemaAvailability({
      status: car?.status,
      availableForSale: car?.availableForSale,
    });

    return {
      '@type': 'ListItem',
      position: (safePage - 1) * safePerPage + index + 1,
      item: {
        '@type': 'Product',
        additionalType: 'https://schema.org/Car',
        '@id': carUrl,
        name: title,
        image: imageUrl,
        url: carUrl,
        brand: {
          '@type': 'Brand',
          name: brandLabel,
        },
        offers: {
          '@type': 'Offer',
          price: priceInfo.numeric || '0',
          priceCurrency: 'THB',
          url: carUrl,
          itemCondition: 'https://schema.org/UsedCondition',
          availability: `https://schema.org/${availabilityValue}`,
          inventoryLevel: {
            '@type': 'QuantitativeValue',
            value: availabilityValue === 'InStock' ? 1 : 0,
            unitCode: 'EA',
          },
          seller: COMMON_OFFER_EXTENSIONS.seller,
          hasMerchantReturnPolicy: COMMON_OFFER_EXTENSIONS.hasMerchantReturnPolicy,
          shippingDetails: COMMON_OFFER_EXTENSIONS.shippingDetails,
        },
      },
    };
  });

  const pageUrl = `${SITE}/used-cars-chiang-mai-brand/${brandSlug}${safePage > 1 ? `?page=${safePage}` : ''}`;

  const collectionPage = {
    '@type': 'CollectionPage',
    '@id': `${SITE}/used-cars-chiang-mai-brand/${brandSlug}#collection`,
    url: pageUrl,
    name: `รถมือสอง ${brandLabel} เชียงใหม่${safePage > 1 ? ` - หน้า ${safePage}` : ''}`,
    description: `รวมรถมือสอง ${brandLabel} ในเชียงใหม่ พร้อมรูปจริงและลิงก์ไปหน้ารายละเอียดรถ`,
    mainEntity: {
      '@type': 'ItemList',
      name: `รถมือสอง ${brandLabel} เชียงใหม่`,
      numberOfItems: listItems.length,
      itemListElement: listItems,
    },
  };

  const faqs = buildBrandFaqEntries(brandLabel);
  const faqPage = {
    '@type': 'FAQPage',
    '@id': `${SITE}/used-cars-chiang-mai-brand/${brandSlug}#faq`,
    url: pageUrl,
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [collectionPage, faqPage],
  };
}

export async function getServerSideProps(context) {
  const brandInfo = getBrandInfo(context?.params?.brand);
  if (!brandInfo) {
    return { notFound: true };
  }

  const q = context?.query || {};
  const page = Math.max(1, parseInt(String(q.page || '1'), 10) || 1);
  const perPage = 24;

  let cars = [];
  try {
    // Run both fetches in parallel to reduce SSR latency
    const [result, carStatuses] = await Promise.all([
      getAllCars().catch(() => []),
      readCarStatuses().catch(() => null),
    ]);
    const allCars = (Array.isArray(result) ? result : []).filter(c => !isEvCar(c));

    const tokens = brandInfo.tokens;
    cars = allCars
      .filter(car => {
        const hay = `${car?.vendor || ''} ${car?.brand || ''} ${car?.title || ''}`.toLowerCase();
        return tokens.some(t => hay.includes(t));
      })
      .map(car => ({
        id: car.id,
        handle: car.handle,
        title: car.title,
        vendor: car.vendor,
        tags: car.tags,
        price: car.price,
        // Keep quick specs for CarCard (ปี/ไมล์/เกียร์/เชื้อเพลิง)
        year: car.year,
        mileage: car.mileage,
        transmission: car.transmission,
        drivetrain: car.drivetrain || car.drive_type,
        drive_type: car.drive_type || car.drivetrain,
        installment: car.installment,
        fuelType: car.fuelType || car.fuel_type,
        fuel_type: car.fuel_type || car.fuelType,
        category: car.category,
        body_type: car.body_type,
        images: Array.isArray(car.images) ? car.images.slice(0, 1) : [],
        availableForSale: car.availableForSale,
        status: carStatuses && car.id ? carStatuses[car.id]?.status || 'available' : 'available',
      }));
  } catch {
    cars = [];
  }

  const totalCars = cars.length;
  const totalPages = Math.max(1, Math.ceil(totalCars / perPage));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * perPage;
  let pageCars = cars.slice(start, start + perPage);

  if (context?.res?.setHeader) {
    context.res.setHeader(
      'Cache-Control',
      'public, s-maxage=600, stale-while-revalidate=1800, stale-if-error=86400'
    );
  }

  const structuredData = buildBrandStructuredDataJsonLd({
    brandLabel: brandInfo.label,
    brandSlug: brandInfo.slug,
    cars: pageCars,
    page: safePage,
    perPage,
  });

  const homeOgImage = `${SITE}/api/og?src=${encodeURIComponent(
    '/herobanner/cnxcar.webp'
  )}&w=1200&h=630`;

  return {
    props: {
      brandInfo,
      cars: pageCars,
      totalCars,
      safePage,
      totalPages,
      homeOgImage,
      structuredData,
    },
  };
}

export default function UsedCarsChiangMaiBrand({
  brandInfo,
  cars,
  totalCars,
  safePage,
  totalPages,
  homeOgImage,
  structuredData,
}) {
  const router = useRouter();
  const safeCars = useMemo(() => (Array.isArray(cars) ? cars : []), [cars]);

  const brandFaqs = useMemo(() => buildBrandFaqEntries(brandInfo?.label), [brandInfo?.label]);

  const isEmpty = !totalCars || totalCars <= 0;
  const url = `/used-cars-chiang-mai-brand/${brandInfo.slug}${
    safePage > 1 ? `?page=${safePage}` : ''
  }`;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={`รถมือสอง ${brandInfo.label} เชียงใหม่ สภาพสวย ฟรีดาวน์${safePage > 1 ? ` หน้า ${safePage}` : ''} | ครูหนึ่งรถสวย`}
        description={`รวมรถ ${brandInfo.label} มือสอง เชียงใหม่ สภาพนางฟ้า ไมล์แท้ ฟรีดาวน์ มีให้เลือก ${totalCars} คัน รับประกันเครื่องยนต์และเกียร์ ออกรถ 0 บาท ที่ครูหนึ่งรถสวย`}
        url={url}
        image={homeOgImage}
        type="website"
        pageType="all-cars"
        noindex={process.env.NODE_ENV === 'production' && isEmpty}
        breadcrumbs={[
          { name: 'หน้าแรก', url: '/' },
          { name: 'รถมือสอง เชียงใหม่', url: '/used-cars-chiang-mai' },
          {
            name: `รถมือสอง ${brandInfo.label} เชียงใหม่`,
            url: `/used-cars-chiang-mai-brand/${brandInfo.slug}`,
          },
        ]}
        structuredData={structuredData}
      />

      {/* Hero Banner Area */}
      <header
        id="hero"
        className="relative w-full overflow-hidden bg-gray-900 border-b border-gray-200"
      >
        <div className="relative w-full aspect-[16/10] xs:aspect-[16/9] sm:aspect-[1920/800] md:aspect-[1400/474]">
          <picture>
            <source
              media="(max-width: 767px)"
              srcSet="/herobanner/cnxallcar-480w.webp 480w, /herobanner/cnxallcar-640w.webp 640w, /herobanner/cnxallcar-828w.webp 828w"
              sizes="100vw"
            />
            <source
              media="(min-width: 768px)"
              srcSet="/herobanner/cnxallcar-828w.webp 828w, /herobanner/cnxallcar-1024w.webp 1024w, /herobanner/cnxallcar-1400w.webp 1400w"
              sizes="100vw"
            />
            <img
              src="/herobanner/cnxallcar-1024w.webp"
              alt={`รถมือสอง ${brandInfo.label} เชียงใหม่ - ครูหนึ่งรถสวย`}
              className="block w-full h-full object-cover object-center"
              decoding="async"
              fetchpriority="high"
            />
          </picture>

          {/* Dark gradient overlay to make text more readable */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70 sm:from-black/50 sm:to-black/60"></div>

          {/* Text Content Overlay */}
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-4xl mx-auto text-center rounded-2xl bg-black/40 sm:bg-black/50 backdrop-blur-sm p-4 sm:p-6 md:p-8 outline outline-1 outline-white/20 shadow-2xl">
              <h1
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white font-prompt leading-tight"
                style={{
                  textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 4px 12px rgba(0,0,0,0.6)',
                }}
              >
                รถมือสอง {brandInfo.label} เชียงใหม่ คัดสภาพสวย
              </h1>
              <p
                className="mt-2 sm:mt-3 text-sm sm:text-base md:text-lg text-white/90 font-prompt leading-relaxed max-w-2xl mx-auto"
                style={{
                  textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                }}
              >
                รวมรถมือสอง {brandInfo.label} คุณภาพดี สภาพนางฟ้า ฟรีดาวน์ ไมล์แท้
                พร้อมตรวจสอบประวัติได้ ราคาอัปเดต และลิงก์ไปหน้ารายละเอียดรถ
              </p>
              <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-center gap-2 sm:gap-3">
                <Link
                  href="/all-cars"
                  className="px-5 py-2.5 sm:px-6 sm:py-3 bg-primary hover:bg-primary-dark text-white rounded-lg font-bold font-prompt transition-colors text-sm sm:text-base border border-white/10"
                >
                  ดูรถทั้งหมด
                </Link>
                <Link
                  href="/used-cars-chiang-mai"
                  className="px-5 py-2.5 sm:px-6 sm:py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg font-bold font-prompt backdrop-blur-md transition-colors text-sm sm:text-base border border-white/30"
                >
                  รวมรถมือสองเชียงใหม่
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      <Breadcrumb />

      <main className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 ipadpro:px-3 py-8">
        {/* ปุ่มย้อนกลับ */}
        <div className="mb-4">
          <button
            onClick={() => {
              if (window.history.length > 2) {
                router?.back();
              } else {
                router.push('/all-cars');
              }
            }}
            className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-primary transition-colors focus:outline-none group font-prompt"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="mr-1.5 h-4 w-4 transform group-hover:-translate-x-1 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            ย้อนกลับ
          </button>
        </div>

        <section className="mb-4">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-primary font-prompt">
                รายการรถ {brandInfo.label} ในเชียงใหม่
              </h2>
              <p className="text-gray-700 mt-2 font-prompt">
                ทั้งหมด {totalCars.toLocaleString('th-TH')} คัน{' '}
                {totalPages > 1 ? `• หน้า ${safePage}/${totalPages}` : ''}
              </p>
            </div>
            <div className="flex gap-2">
              <Link
                href={`/used-cars-chiang-mai-brand/${brandInfo.slug}${safePage > 1 ? `?page=${safePage - 1}` : ''}`}
                className={`inline-flex items-center justify-center rounded-xl border px-3 py-2 text-xs font-semibold font-prompt transition-colors ${safePage <= 1 ? 'border-gray-300 text-gray-500 pointer-events-none' : 'border-primary text-primary hover:bg-primary hover:text-white'}`}
                aria-disabled={safePage <= 1}
              >
                ก่อนหน้า
              </Link>
              <Link
                href={`/used-cars-chiang-mai-brand/${brandInfo.slug}${safePage < totalPages ? `?page=${safePage + 1}` : ''}`}
                className={`inline-flex items-center justify-center rounded-xl border px-3 py-2 text-xs font-semibold font-prompt transition-colors ${safePage >= totalPages ? 'border-gray-300 text-gray-500 pointer-events-none' : 'border-primary text-primary hover:bg-primary hover:text-white'}`}
                aria-disabled={safePage >= totalPages}
              >
                ถัดไป
              </Link>
            </div>
          </div>
        </section>

        {safeCars.length === 0 ? (
          <section className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 p-5">
            <div className="font-semibold text-gray-900 font-prompt">
              ยังไม่มีรถยี่ห้อนี้ในสต็อกตอนนี้
            </div>
            <div className="mt-2 text-sm text-gray-700 font-prompt">
              ลองดูหน้า “รถทั้งหมด” หรือหน้า “รถมือสองเชียงใหม่” เพื่อดูรถที่มีในสต็อกล่าสุด
            </div>
            <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3">
              <Link
                href="/all-cars"
                className="btn-primary text-center w-full sm:w-auto px-6 py-2.5"
              >
                ดูรถทั้งหมด
              </Link>
              <Link
                href="/used-cars-chiang-mai"
                className="btn-secondary text-center rounded-xl w-full sm:w-auto px-6 py-2.5"
              >
                รถมือสองเชียงใหม่
              </Link>
            </div>
          </section>
        ) : (
          <section className="mt-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4 lg:gap-4 xl:gap-6">
              {safeCars.map((car, index) => {
                return <CarCard key={car?.id || index} car={car} priority={index < 6} />;
              })}
            </div>
          </section>
        )}

        <section className="mt-8 bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-bold text-primary font-prompt">
            รู้จัก {brandInfo.label} มือสองเชียงใหม่
          </h2>
          <p className="mt-3 text-gray-700 font-prompt leading-relaxed">
            {BRAND_DESCRIPTIONS[brandInfo.slug]}
          </p>
          <p className="mt-3 text-gray-700 font-prompt leading-relaxed">
            ที่ครูหนึ่งรถสวย เราคัดสรรรถมือสอง {brandInfo.label} สภาพดี ไมล์แท้
            ตรวจสอบประวัติรถทุกคัน พร้อมรับประกันเครื่องยนต์และเกียร์ 1 ปี ฟรีดาวน์ 0% ผ่อนสบาย
            และจัดส่งฟรีทั่วประเทศ
          </p>
        </section>

        <section className="mt-8 bg-white rounded-2xl border border-gray-200 p-5 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-bold text-primary font-prompt">ยี่ห้ออื่น ๆ</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {Object.keys(BRAND_CONFIG).map(slug => (
              <Link
                key={slug}
                href={`/used-cars-chiang-mai-brand/${slug}`}
                className={`inline-flex items-center justify-center rounded-full border px-4 py-2 text-sm font-semibold font-prompt transition-colors ${slug === brandInfo.slug ? 'bg-primary text-white border-primary' : 'border-gray-300 text-gray-800 hover:border-primary hover:text-primary'}`}
              >
                {BRAND_CONFIG[slug].label}
              </Link>
            ))}
          </div>
        </section>

        <section
          id="faq"
          className="mt-8 overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm"
        >
          <div className="bg-gradient-to-r from-primary to-primary-800 px-5 py-4 sm:px-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white font-prompt">คำถามที่พบบ่อย</h2>
            <p className="mt-1 text-sm text-white/90 font-prompt">
              คำตอบสั้นๆ ช่วยตัดสินใจดูรถได้เร็วขึ้น
            </p>
          </div>

          <div className="p-5 sm:p-6">
            <div className="space-y-3 font-prompt">
              {brandFaqs.map((f, idx) => (
                <details
                  key={f.question}
                  className="group rounded-2xl border border-gray-200 bg-white px-4 sm:px-5 py-4"
                >
                  <summary className="flex cursor-pointer list-none items-start gap-3">
                    <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent-900">
                      <span className="text-sm font-bold">{idx + 1}</span>
                    </span>

                    <div className="min-w-0 flex-1">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                        {f.question}
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">กดเพื่อดูคำตอบ</p>
                    </div>

                    <span className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-700 transition-transform group-open:rotate-180">
                      <svg
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </span>
                  </summary>

                  <div className="mt-3 text-gray-700 text-sm sm:text-base leading-relaxed">
                    {f.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
